import type {RouteConfig} from "@lit-labs/router";
import {html, TemplateResult} from "lit";
import {appState} from "../../app.state";
import {NavigationHelper} from "../../helpers/navigation.helper";
import {container} from "tsyringe";
import {IssueRepository} from "../../domain/repositories/issue.repository";
import {IssueHelper} from "../../domain/helpers/issue.helper";
import {AuthorizationService} from "../../services/authorization.service";
import {UiHelper} from "../../helpers/ui.helper";
import {NOTIFICATION_DURATION} from "../../constants";
import {FEATURE_FLAGS} from "../../feature-flags";
import {isTenantThemeEnabled} from "../../tenant-feature-flags";
import {hasPrivilege} from "../../helpers/privilege.helper";
import {CommandName} from "../../commands/command-name.enum";
import {buildRoutes} from "./route-filters";
import {requirePrivilege} from "./route-guards";
import {homeTarget} from "./start-page.helper";

// Inverse-only guard for /auth/login: if the user is already authenticated,
// bounce them off the login screen to the home route. Access to protected
// routes is governed by the auth-gate in ha-app.render(), not here, so this
// no longer needs awaitAuthHydration() or redirectAfterLogin capture.
const redirectIfAlreadyLoggedIn = (): boolean => {
    if (appState.isUserLoggedIn) {
        NavigationHelper.goto('/');
        return false;
    }

    return true;
}

// Privilege guards for the settings routes: block access when the user lacks the privilege,
// bouncing them to the home route (route-guards.ts — pure, unit tested there).
const requireTranslationOverridePrivilege = requirePrivilege(
    CommandName.SAVE_TRANSLATION_OVERRIDE, hasPrivilege, path => NavigationHelper.goto(path),
);
const requireTenantThemePrivilege = requirePrivilege(
    CommandName.SAVE_TENANT_THEME, hasPrivilege, path => NavigationHelper.goto(path),
);
const requireTenantSettingsPrivilege = requirePrivilege(
    CommandName.SAVE_TENANT_SETTINGS, hasPrivilege, path => NavigationHelper.goto(path),
);

// Exported only for page-title.keys.test.ts (every view has its pageTitle text); the app uses
// the flag-filtered `routes` below.
export const allRoutes: RouteConfig[] = [
    {
        name: 'login',
        path: '/auth/login',
        enter: () => redirectIfAlreadyLoggedIn(),
        render: () => html`
            <ha-login-view></ha-login-view>
        `
    },
    {
        name: 'logout',
        path: '/auth/logout',
        enter: () => {
            container.resolve(AuthorizationService).logout();
            return false;
        }
    },
    {
        name: 'by-board',
        path: '/by-board',
        render: () => html`
            <o2d-boards-view></o2d-boards-view>
        `
    },
    {
        name: 'by-daily',
        path: '/by-daily',
        render: () => html`
            <o2d-dailys-view></o2d-dailys-view>
        `
    },
    {
        name: 'home',
        path: '/',
        // Auth is governed by the auth-gate in ha-app.render(); the home route
        // only resolves the landing view: the tenant's saved start page if there
        // is one — it always wins (feature plan 0068) — else the last used mode.
        // The rules live in start-page.helper.ts (pure, unit tested there).
        enter: () => {
            NavigationHelper.goto(homeTarget(window.__TENANT_SETTINGS__, localStorage.getItem('lastMode')));
            // goto's pushState fires no popstate, so the ModeRouterController would keep the mode
            // it had before the redirect and the mode switch would mark "ToDo" (ticket
            // AaB6wbGhcsuaYRg_KD8nWw). Same synthetic popstate as the /by-resource-planning
            // redirect below; the router's own popstate listener re-goto's the same URL.
            window.dispatchEvent(new PopStateEvent('popstate'));
            return false;
        }
    },
    {
        name: 'by-assignee',
        path: '/by-assignee',
        render: () => html`
            <o2d-assignees-view></o2d-assignees-view>
        `
    },
    {
        // Legacy path — ALWAYS client-side redirects to the canonical
        // /resource-management (O2D-PRD-100, TR3), preserving old bookmarks.
        // @lit-labs/router 0.1.4 has no declarative `redirect`, so this mirrors
        // the `wissen-overview-redirect` enter()-guard idiom below.
        // NavigationHelper.goto uses history.pushState, which fires NO popstate,
        // so the ModeRouterController would not re-derive the mode on its own.
        // We dispatch a synthetic popstate to trigger its URL re-sync against the
        // FINAL /resource-management URL, so appState.currentMode becomes
        // 'resource' (mirrors ha-mode-switch._onClick calling syncFromUrl after
        // goto; the controller stays the SOLE writer of currentMode, TD-002 F6).
        // The router's own popstate listener re-goto's to the same URL — idempotent.
        name: 'by-resource-planning-redirect',
        path: '/by-resource-planning',
        enter: () => {
            NavigationHelper.goto('/resource-management');
            window.dispatchEvent(new PopStateEvent('popstate'));
            return false;
        }
    },
    {
        // Canonical route for the resource-management mode. Renders the
        // <o2d-resource-planning-view>; the legacy /by-resource-planning path
        // above client-side-redirects here so both URLs land on the mode-owned
        // /resource-management.
        name: 'resource-management-view',
        path: '/resource-management',
        render: () => html`
            <o2d-resource-planning-view></o2d-resource-planning-view>
        `
    },
    {
        // Canonical route for the Linkview mode (Stefan, 2026-09-03). Bare
        // list route — no Linkview selected yet.
        name: 'linkview',
        path: '/linkview',
        render: () => html`
            <o2d-linkview-view></o2d-linkview-view>
        `
    },
    {
        // Anke, 2026-09-04: "es kann mehrere Linkviews geben" — one named
        // Linkview per route, same `<o2d-linkview-view>` element with a
        // one-way `.sid=` binding as `knowledge-object`/`translation-editor`
        // below use for their own `:sid`/`:segment` routes, so the SAME
        // instance is reconciled (not remounted) when switching between two
        // Linkviews via the sidebar list.
        name: 'linkview-detail',
        path: '/linkview/:sid/:slug?',
        render: ({sid}: { [key: string]: string | undefined; }): TemplateResult => html`
            <o2d-linkview-view .sid=${sid}></o2d-linkview-view>
        `
    },
    {
        // "Mein Kalender" — the page "My Organizer" mode opens on (Anke,
        // 2026-10-17: "bitte die menupunkte 'Mein Kalender' und 'Meine Boxen'
        // tauschen. es soll zuerst 'Mein Kalender' ausgewählt sein"), so it
        // owns the mode's root path that the mode switch and the `lastMode`
        // redirect above go to. Sibling: 'personal-organizer-boxes' below —
        // both are listed in the mode's own sidebar submenu
        // (`_personalOrganizerNavItems` in ha-app.component.ts), same pattern
        // as resource-mode's `_resourceNavItems`.
        name: 'personal-organizer-calendar',
        path: '/personal-organizer',
        render: () => html`
            <o2d-personal-organizer-calendar-view></o2d-personal-organizer-calendar-view>
        `
    },
    {
        // "Meine Boxen" — the second "My Organizer" page (personal, per-User
        // labels/time-cluster/date on todo Issues — data foundation shipped in
        // docs/feature-plans/0026-personal-labels-issue-data/). Was the root
        // page until 2026-10-17. `startsWith('/personal-organizer')` in
        // mode-router.controller.ts already covers this nested path, so it
        // stays in the same 'personal-organizer' mode as its sibling above.
        name: 'personal-organizer-boxes',
        path: '/personal-organizer/boxes',
        render: () => html`
            <o2d-personal-organizer-view></o2d-personal-organizer-view>
        `
    },
    {
        // Legacy path of "Mein Kalender" (until 2026-10-17) — redirects to its
        // new home, preserving old bookmarks. Same enter()-guard idiom as
        // `wissen-overview-redirect` below; the mode stays 'personal-organizer',
        // so no synthetic popstate is needed (unlike the resource redirect).
        name: 'personal-organizer-calendar-redirect',
        path: '/personal-organizer/calendar',
        enter: () => {
            NavigationHelper.goto('/personal-organizer');
            return false;
        }
    },
    {
        // Mode "Gimmicks" (Anke, 2026-11-01): one page, laid out like the Linkview
        // page; the gimmicks themselves (stopwatch, random pick, ...) follow.
        name: 'gimmicks',
        path: '/gimmicks',
        render: () => html`
            <o2d-gimmicks-view></o2d-gimmicks-view>
        `
    },
    {
        name: 'by-label',
        path: '/by-label',
        render: () => html`
            <o2d-labels-view></o2d-labels-view>
        `
    },
    {
        name: 'by-type',
        path: '/by-type',
        render: () => html`
            <o2d-types-view></o2d-types-view>
        `
    },
    {
        name: 'by-issue-status',
        path: '/by-issue-status',
        render: () => html`
            <o2d-issue-status-view></o2d-issue-status-view>
        `
    },
    {
        name: 'by-creator',
        path: '/by-creator',
        render: () => html`
            <o2d-creators-view></o2d-creators-view>
        `
    },
    {
        name: 'tasklist',
        path: '/tasklist',
        render: () => html`
            <ha-tasklist-view></ha-tasklist-view>
        `
    },
    {
        // /tasklist split into two views (regular tasks vs. absences/public
        // holidays) sharing one component with a mode switch — see
        // ha-tasklist.view.ts's `mode` property.
        name: 'absence-list',
        path: '/absence-list',
        render: () => html`
            <ha-tasklist-view mode="absences"></ha-tasklist-view>
        `
    },
    {
        name: 'custom',
        path: '/by-me',
        render: () => html`
            <ha-user-view></ha-user-view>
        `
    },
    {
        name: 'quicklink-to-issue',
        path: '/issue/:uid',
        render: ({uid}: { [key: string]: string | undefined; }): TemplateResult => {
            if (!uid) {
                return html``;
            }

            container.resolve(IssueRepository).findOneById(uid).then(issue => {
                if (!issue) {
                    UiHelper.notify({
                        title: `Vorgang nicht gefunden`,
                        content: html`
                            <p>Der Vorgang mit der UID «${uid}» konnte nicht gefunden werden. Ist dieser
                                eventuell bereits gelöscht?</p>
                        `,
                        closable: true,
                        duration: NOTIFICATION_DURATION,
                        level: "error"
                    })
                    return;
                }

                void IssueHelper.openIssueEditDialog(issue, {
                    onClose: () => {
                        window.dispatchEvent(
                            new CustomEvent('va.router.goto', {detail: {value: '/'}})
                        );
                    }
                });
            });

            return html``;
        }
    },
    {
        name: 'knowledge',
        path: '/knowledge',
        render: () => html`
            <knowledge-overview-view></knowledge-overview-view>
        `
    },
    {
        name: 'knowledge-object',
        path: '/knowledge/:sid/:slug?',
        render: ({sid}: { [key: string]: string | undefined; }): TemplateResult => html`
            <knowledge-overview-view .sid=${sid}></knowledge-overview-view>
        `
    },
    {
        name: 'wissen-overview-redirect',
        path: '/wissen/overview',
        enter: () => {
            NavigationHelper.goto('/knowledge');
            return false;
        }
    },
    {
        name: 'wissen-object-redirect',
        path: '/wissen/objects/:sid',
        enter: ({sid}: { [key: string]: string | undefined; }) => {
            NavigationHelper.goto(`/knowledge/${sid}`);
            return false;
        }
    },
    {
        name: 'translation-editor',
        path: '/settings/translations/:segment?',
        enter: () => requireTranslationOverridePrivilege(),
        // Single route for both `/settings/translations` and
        // `/settings/translations/:segment`: it renders the SAME
        // `<ha-translation-editor-view>` tag for every `:segment` with a one-way
        // `.segment=` binding (mirror of `.sid=` above), so the single instance
        // is reconciled on segment change, NOT remounted — draft state survives.
        render: ({segment}: { [key: string]: string | undefined; }): TemplateResult => html`
            <ha-translation-editor-view .segment=${segment}></ha-translation-editor-view>
        `
    },
    {
        // Tenant theme stylesheet editor (feature plan 0054). Present only while the tenant's
        // FEATURE_TENANT_THEME_ENABLED is on — buildRoutes() filters it out otherwise.
        name: 'tenant-theme-editor',
        path: '/settings/theme',
        enter: () => requireTenantThemePrivilege(),
        render: (): TemplateResult => html`
            <ha-tenant-theme-editor-view></ha-tenant-theme-editor-view>
        `
    },
    {
        // Settings page "Allgemein" (feature plan 0068): tenant-wide settings, today the start page.
        // Always present — no runtime flag — but behind its own privilege.
        name: 'tenant-settings',
        path: '/settings/general',
        enter: () => requireTenantSettingsPrivilege(),
        render: (): TemplateResult => html`
            <ha-tenant-settings-view></ha-tenant-settings-view>
        `
    },
    {
        // Settings page "Mein Profil": every logged-in user changes their own name and e-mail (self
        // policy), so no privilege guard.
        name: 'own-profile',
        path: '/settings/profile',
        render: (): TemplateResult => html`
            <ha-own-profile-view></ha-own-profile-view>
        `
    },
    {
        name: 'fallback',
        path: '/(.*)',
        render: () => html`
            <ha-not-found></ha-not-found>
        `
    }
]

// Reads the flags and calls buildRoutes() (route-filters.ts — pure logic, unit tested
// there directly) at MODULE EVALUATION TIME (a top-level const). That is intentional: `routes` has to be a
// single stable array reference handed to the Router constructor once, not recomputed per
// render. It is still correct per-tenant: window.__FEATURE_FLAGS__ is injected fresh into
// the HTML by HomeAction.php on every full page load (before this module's <script
// type="module"> tag even runs, per views/index.html's ordering), and this app's
// multi-tenancy is subdomain-based (nginx maps Host -> TENANT) — switching tenants always
// means a full browser navigation to a different origin, never an SPA-internal route
// change. So there is no in-session window where this module's already-evaluated `routes`
// could reflect a DIFFERENT tenant's flag than the one the page was actually loaded for;
// the same reasoning already applies to window.__APP_CONTEXT__ and the existing
// FEATURE_FLAGS.myView build-time flag, both consumed the same "read once, valid for the
// life of this page load" way. The actual filtering logic itself (buildRoutes,
// route-filters.ts) is covered directly by route-filters.test.ts — this wiring line is the
// only untested part, and it's a one-line pass-through of already-tested logic.
export const routes: RouteConfig[] = buildRoutes(
    FEATURE_FLAGS.myView,
    isTenantThemeEnabled(window.__FEATURE_FLAGS__),
    allRoutes,
)